import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { TAnswers } from 'models/common.model'
import { LOCALES } from 'root-constants'
import { getAnswer } from 'nove8/helpers/getAnswer'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { IDK, YES_ANSWER } from './pages/constants/constants'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const getAnswers = (): Partial<TAnswers> | null =>
  useZustandSessionStore.getState().userAnswers

const shouldSkipOnePageOnFeelGuiltyAnswer = () => {
  const savedAnswers = getAnswers()

  if (!savedAnswers) return false

  const feelGuiltyAnswer = getAnswer(
    savedAnswers[PageId.FEEL_GUILTY_LOVE] as string,
  )

  if (feelGuiltyAnswer === IDK) {
    return true
  }

  return false
}
const shouldSkipThreePagesOnSatisfiedQuestion = () => {
  const savedAnswers = getAnswers()

  if (!savedAnswers) return false

  const satisfiedAnswer = getAnswer(
    savedAnswers[PageId.SATISFIED_LOVE] as string,
  )

  if (satisfiedAnswer === YES_ANSWER) {
    return true
  }

  return false
}

const isEnLocale = () => {
  const langFromUrl = new URL(window.location.href).searchParams.get('lang')

  return (
    (langFromUrl || useZustandSessionStore.getState().language) === LOCALES.EN
  )
}

export const getXStateConfigWoofzLove = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_LOVE

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''

          return pageIdFromUrl === PageId.URGE_1_LOVE && !isEnLocale()
        },
        target: PageId.TALKING_LOVE,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_LOVE,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_LOVE,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.FEEL_GUILTY_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnFeelGuiltyAnswer,
                target: PageId.DOG_REACT_LOVE,
              },
              {
                target: PageId.URGE_3_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.FEELINGS_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.URGE_1_LOVE,
              },
              {
                target: PageId.TALKING_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.TALKING_LOVE && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.URGE_1_LOVE,
              },
              {
                target: PageId.FEELINGS_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_REACT_LOVE && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnFeelGuiltyAnswer,
                target: PageId.FEEL_GUILTY_LOVE,
              },
              {
                target: PageId.URGE_3_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.SATISFIED_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipThreePagesOnSatisfiedQuestion,
                target: PageId.DOG_BREED_LOVE,
              },
              {
                target: PageId.BEHAVIOR_ISSUES_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_LOVE && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipThreePagesOnSatisfiedQuestion,
                target: PageId.SATISFIED_LOVE,
              },
              {
                target: PageId.URGE_4_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_PROFILE_LOVE && {
            PREVIOUS_FLOW_STEP: {
              target: PageId.DOG_NAME_LOVE,
            },
          }),
          ...(page.pageId === PageId.EMAIL_LOVE && {
            PREVIOUS_FLOW_STEP: {
              target: PageId.FREQUENCY_LOVE,
            },
          }),
          ...(page.pageId === PageId.CANCEL_OFFER_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_LOVE,
              },
            ],
            PREVIOUS_FLOW_STEP: {
              target: PageId.CANCEL_OFFER_LOVE,
            },
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_LOVE,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_LOVE,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_LOVE && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_LOVE,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_LOVE && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_NOVE8,
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_LOVE && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_LOVE,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.DOWNLOAD_LOVE]: {
    type: 'final',
  },
})
